import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { customFetch } from '@/lib/api/client';
import {
  archiveAllNotifications,
  changeNotifications,
  getCountNotificationsUrl,
  getListNotificationsUrl,
  markAllNotificationsRead,
  type countNotificationsResponse,
  type listNotificationsResponse,
} from '@/lib/api/generated/api';
import type {
  InboxAction,
  InboxChange,
  NotificationPage,
} from '@/lib/api/generated/model';

import type { InboxQuery } from '../pageFilter';
import { notificationKeys } from '../queryKeys';
import { NotificationRequestError, reportNotificationProblem } from '../report';

export const PAGE_SIZE = 50;

/**
 * The generated URL builders write an array as one comma-joined value
 * (`kind=a,b`). The service reads that too now, but repeating `kind` is its
 * documented form, so the kinds are appended here, one each.
 */
export function withKinds(url: string, kinds?: readonly string[]): string {
  if (!kinds || kinds.length === 0) return url;
  const [path = '', search = ''] = url.split('?');
  const params = new URLSearchParams(search);
  for (const kind of kinds) params.append('kind', kind);
  return `${path}?${params.toString()}`;
}

/**
 * The notifications page's list (spec 147 FR-003): one filter, newest first,
 * 50 at a time behind Show older. It does not refetch on focus or on a new
 * arrival; the page says "N new" and refetches when asked (FR-007).
 */
export function useNotificationsPage(query: InboxQuery) {
  const result = useInfiniteQuery({
    queryKey: notificationKeys.page(query),
    queryFn: async ({ pageParam }): Promise<NotificationPage> => {
      const { kind, ...rest } = query;
      const response = await customFetch<listNotificationsResponse>(
        withKinds(
          getListNotificationsUrl({
            ...rest,
            limit: PAGE_SIZE,
            before: pageParam,
          }),
          kind,
        ),
      );
      if (response.status !== 200) {
        reportNotificationProblem('page failed to load', {
          status: response.status,
        });
        throw new NotificationRequestError('list', response.status);
      }
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_before ?? undefined,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    notifications: result.data?.pages.flatMap((page) => page.items),
    isPending: result.isPending,
    loadFailed: result.isError,
    refetch: result.refetch,
    hasMore: result.hasNextPage,
    loadMore: result.fetchNextPage,
    loadingMore: result.isFetchingNextPage,
    /** When the first page was last read: where the next arrivals start. */
    loadedAt: result.dataUpdatedAt,
  };
}

/**
 * How many notifications a filter matches across every page (service spec
 * 130 FR-002). Asked only while something needs it: "Select all {n}
 * matching" and the whole-filter confirmations.
 */
export function useMatchingCount(query: InboxQuery, enabled: boolean) {
  const result = useQuery({
    queryKey: notificationKeys.matching(query),
    queryFn: async () => {
      const { kind, ...rest } = query;
      const response = await customFetch<countNotificationsResponse>(
        withKinds(getCountNotificationsUrl(rest), kind),
      );
      if (response.status !== 200) {
        reportNotificationProblem('count failed to load', {
          status: response.status,
        });
        throw new NotificationRequestError('count', response.status);
      }
      return response.data.total;
    },
    enabled,
    staleTime: 0,
    retry: false,
  });
  return {
    total: result.data,
    isPending: result.isPending,
    loadFailed: result.isError,
  };
}

/**
 * How many notifications the page's filter matches that arrived after the list
 * last loaded (spec 147 FR-007). An arrival names only its kind, so with any
 * other filter set the page cannot tell on its own whether one belongs; the
 * service counts them by the same filter instead. `arrivalSeq` asks again as
 * each new arrival comes in.
 */
export function useArrivedCount(
  query: InboxQuery,
  since: string,
  arrivalSeq: number,
  enabled: boolean,
) {
  const from =
    query.created_from && query.created_from > since
      ? query.created_from
      : since;
  const counted = { ...query, created_from: from };
  const result = useQuery({
    queryKey: notificationKeys.arrived(counted, arrivalSeq),
    queryFn: async () => {
      const { kind, ...rest } = counted;
      const response = await customFetch<countNotificationsResponse>(
        withKinds(getCountNotificationsUrl(rest), kind),
      );
      if (response.status !== 200) {
        reportNotificationProblem('arrivals failed to count', {
          status: response.status,
        });
        throw new NotificationRequestError('count', response.status);
      }
      return response.data.total;
    },
    enabled,
    retry: false,
  });
  return result.data;
}

export type InboxChangeRequest =
  | { kind: 'bulk'; ids: string[]; action: InboxAction }
  | { kind: 'read-all'; filter: InboxQuery }
  | { kind: 'archive-all'; filter: InboxQuery };

/**
 * A bulk change whose later request was refused after earlier ones went
 * through: some notifications did change, and `done` says how many.
 */
export class PartialChangeError extends NotificationRequestError {
  constructor(
    action: string,
    status: number,
    readonly done: InboxChange,
  ) {
    super(action, status);
    this.name = 'PartialChangeError';
  }
}

/** The most ids one `/bulk` request takes (service spec 130). */
const BULK_LIMIT = 200;

async function sendChange(request: InboxChangeRequest): Promise<InboxChange> {
  if (request.kind === 'bulk') {
    // More than one request only past 200 loaded rows; the last answer's
    // count is the one after all of them.
    let result: InboxChange = { unread: 0, changed: 0 };
    for (let i = 0; i < request.ids.length; i += BULK_LIMIT) {
      const response = await changeNotifications({
        ids: request.ids.slice(i, i + BULK_LIMIT),
        action: request.action,
      });
      if (response.status !== 200) {
        if (result.changed > 0) {
          throw new PartialChangeError(request.action, response.status, result);
        }
        throw new NotificationRequestError(request.action, response.status);
      }
      result = {
        unread: response.data.unread,
        changed: result.changed + response.data.changed,
      };
    }
    return result;
  }
  const response =
    request.kind === 'read-all'
      ? await markAllNotificationsRead(request.filter)
      : await archiveAllNotifications(request.filter);
  if (response.status !== 200) {
    throw new NotificationRequestError(request.kind, response.status);
  }
  return response.data;
}

const PARTLY_FAILED = (changed: number) =>
  `${String(changed)} changed, but the rest could not be. Try again for the others.`;

const FAILED: Record<InboxAction | 'read-all' | 'archive-all', string> = {
  read: 'Those notifications could not be marked read.',
  unread: 'Those notifications could not be marked unread.',
  archive: 'Those notifications could not be archived.',
  unarchive: 'Those notifications could not be unarchived.',
  'read-all': 'Notifications could not be marked read.',
  'archive-all': 'Notifications could not be archived.',
};

/**
 * Changing many at once (spec 147 FR-004): chosen rows through `/bulk`, or
 * everything a filter matches through `/read-all` and `/archive-all`. The
 * badge takes the count each answer carries; the page and the bell re-read.
 */
export function useInboxChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendChange,
    onSuccess: (change) => {
      queryClient.setQueryData(notificationKeys.count(), change.unread);
    },
    onError: (error, request) => {
      reportNotificationProblem('change failed', {
        request: request.kind,
        action: request.kind === 'bulk' ? request.action : undefined,
        status:
          error instanceof NotificationRequestError ? error.status : undefined,
        changed:
          error instanceof PartialChangeError ? error.done.changed : undefined,
        error: String(error),
      });
      if (error instanceof PartialChangeError) {
        queryClient.setQueryData(notificationKeys.count(), error.done.unread);
        toast.error(PARTLY_FAILED(error.done.changed));
        return;
      }
      toast.error(
        FAILED[request.kind === 'bulk' ? request.action : request.kind],
      );
    },
    onSettled: () => {
      for (const queryKey of [
        notificationKeys.lists(),
        notificationKeys.pages(),
        notificationKeys.matchings(),
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
